import useGetProjectByIdOverview from '@/api/queries/project/useGetProjectByIdOverview';
import { Card } from '@payaca/ambrosia';

import UntitledIcon from '@payaca/untitled-icons';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import DnoApplicationsCard from './DnoApplicationsCard';
import EnergyPerformanceCertificateCard from './EnergyPerformanceCertificateCard';
import PropertyDetailsCard from './PropertyDetailsCard';
import { SyncEnergyPerformanceCertificateButton } from './SyncEnergyPerformanceCertificateButton';
import { usePropertyPanelVisibility } from './usePropertyPanelVisibility';

/**
 * The Property tab: facts about the building — the manually entered property
 * details, the EPC (UK) and DNO applications. The site address itself lives on
 * the project sidebar; the system designs derived from these facts live on the
 * Design tab.
 */
const PropertyPanel: FC = () => {
  /**
   * Router
   */
  const { projectId } = useParams<{ projectId: string }>();

  const { project } = useGetProjectByIdOverview(projectId);
  const { showEpc, showPropertyDetails, showDnoApplications } =
    usePropertyPanelVisibility();

  const siteAddress = project?.addresses.site[0];
  const epcs = siteAddress?.energyPerformanceCertificates;
  const hasAnyEpcs = !!epcs?.length;

  return (
    <div className="@container space-y-4">
      {!siteAddress && (
        <Card>
          <Card.Body>
            <h3>Property details</h3>
            <p>Add a site address to the project to record its details.</p>
          </Card.Body>
        </Card>
      )}

      {showEpc && !!project?.addresses.site.length && (
        <>
          {!hasAnyEpcs && (
            <Card>
              <Card.Body className="flex justify-between items-start">
                <div>
                  <h3>Energy performance certificate (EPC)</h3>

                  <p>
                    No energy performance certificates have been fetched yet for
                    the project site address
                  </p>
                </div>

                <SyncEnergyPerformanceCertificateButton
                  projectId={projectId}
                  addressId={project.addresses.site[0].publicId}
                  size="sm"
                  variant="secondary"
                >
                  <UntitledIcon name="search-lg.3" className="h-5 w-5" />
                  Search for EPC
                </SyncEnergyPerformanceCertificateButton>
              </Card.Body>
            </Card>
          )}

          {hasAnyEpcs && (
            <EnergyPerformanceCertificateCard
              projectId={projectId}
              siteAddressPublicId={project.addresses.site[0].publicId}
              energyPerformanceCertificate={epcs[0]}
            />
          )}
        </>
      )}

      {showPropertyDetails && projectId && siteAddress && (
        <PropertyDetailsCard
          projectId={projectId}
          addressId={siteAddress.publicId}
          propertyInfo={siteAddress.propertyInformation ?? undefined}
        />
      )}

      {showDnoApplications && <DnoApplicationsCard />}
    </div>
  );
};

export default PropertyPanel;
